import { useRef } from 'react';
import type { UpstreamModelCostRecord } from '@nadoutong/contracts';
import { createModelCostOperationId } from '../../api/upstreamModelCosts.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  ScheduleWindowsEditor,
  type ScheduleWindowsCopy,
} from '../schedule-windows/ScheduleWindowsEditor.tsx';
import {
  draftsToInputs,
  windowToDraft,
  type WeekdayKey,
} from '../schedule-windows/scheduleWindowDraft.ts';
import type { ModelCostIdentity, ModelCostSaveInput } from './ModelCostCell.tsx';

const COST_COPY: ScheduleWindowsCopy = {
  none: 'upstream_detail.routing.cost.windows.none',
  active: 'upstream_detail.routing.cost.windows.active',
  base: 'upstream_detail.routing.cost.windows.base',
  effective: 'upstream_detail.routing.cost.windows.effective',
  configure: 'upstream_detail.routing.cost.windows.configure',
  dialogTitle: 'upstream_detail.routing.cost.windows.dialog_title',
  dialogDescription: 'upstream_detail.routing.cost.windows.dialog_description',
  close: 'upstream_detail.routing.cost.windows.close',
  cancel: 'upstream_detail.routing.cost.windows.cancel',
  save: 'upstream_detail.routing.cost.windows.save',
  add: 'upstream_detail.routing.cost.windows.add',
  remove: 'upstream_detail.routing.cost.windows.remove',
  empty: 'upstream_detail.routing.cost.windows.empty',
  enabled: 'upstream_detail.routing.cost.windows.enabled',
  label: 'upstream_detail.routing.cost.windows.label',
  timezone: 'upstream_detail.routing.cost.windows.timezone',
  days: 'upstream_detail.routing.cost.windows.days',
  day: {
    mon: 'upstream_detail.routing.cost.windows.day.mon',
    tue: 'upstream_detail.routing.cost.windows.day.tue',
    wed: 'upstream_detail.routing.cost.windows.day.wed',
    thu: 'upstream_detail.routing.cost.windows.day.thu',
    fri: 'upstream_detail.routing.cost.windows.day.fri',
    sat: 'upstream_detail.routing.cost.windows.day.sat',
    sun: 'upstream_detail.routing.cost.windows.day.sun',
  } satisfies Record<WeekdayKey, I18nKey>,
  start: 'upstream_detail.routing.cost.windows.start',
  end: 'upstream_detail.routing.cost.windows.end',
  crossMidnight: 'upstream_detail.routing.cost.windows.cross_midnight',
  multiplier: 'upstream_detail.routing.cost.windows.multiplier',
  labelRequired: 'upstream_detail.routing.cost.windows.label_required',
  timezoneRequired: 'upstream_detail.routing.cost.windows.timezone_required',
  daysRequired: 'upstream_detail.routing.cost.windows.days_required',
  clockInvalid: 'upstream_detail.routing.cost.windows.clock_invalid',
  startEndEqual: 'upstream_detail.routing.cost.windows.start_end_equal',
  startNotEndOfDay: 'upstream_detail.routing.cost.windows.start_not_end_of_day',
  multiplierInvalid: 'upstream_detail.routing.cost.windows.multiplier_invalid',
  advanced: 'upstream_detail.routing.cost.windows.advanced',
  input: 'upstream_detail.routing.cost.windows.input',
  output: 'upstream_detail.routing.cost.windows.output',
  cacheRead: 'upstream_detail.routing.cost.windows.cache_read',
  cacheWrite: 'upstream_detail.routing.cost.windows.cache_write',
  reasoning: 'upstream_detail.routing.cost.windows.reasoning',
  needPrice: 'upstream_detail.routing.cost.windows.need_price',
};

export function ModelCostWindowsEditor({
  identity,
  cost,
  costsLoaded,
  saving,
  onSave,
}: {
  readonly identity: ModelCostIdentity;
  readonly cost: UpstreamModelCostRecord | undefined;
  readonly costsLoaded: boolean;
  readonly saving: boolean;
  readonly onSave: (input: ModelCostSaveInput) => Promise<void> | void;
}): JSX.Element {
  const operationRef = useRef<{ fingerprint: string; id: string } | null>(null);
  const activeWindow =
    cost === undefined || cost.active_window_id === null
      ? undefined
      : cost.windows.find((window) => window.window_id === cost.active_window_id);

  return (
    <ScheduleWindowsEditor
      testIdPrefix="upstream-model-cost-windows"
      testIdWindowPrefix="upstream-model-cost-window"
      subjectKey={identity.capabilityId}
      titleParams={{ model: identity.label }}
      copy={COST_COPY}
      baseMultiplier={cost?.cost_multiplier ?? 0}
      effectiveMultiplier={cost?.effective_cost_multiplier ?? 0}
      activeLabel={activeWindow?.label}
      loaded={costsLoaded}
      available={cost !== undefined}
      saving={saving}
      showDimensions
      loadDrafts={() =>
        (cost?.windows ?? []).map((window, index) => windowToDraft(window, `existing-${index}`))
      }
      parseDrafts={draftsToInputs}
      onSave={async (drafts) => {
        if (cost === undefined) return;
        const windows = draftsToInputs(drafts);
        if (windows === null) return;
        const fingerprint = JSON.stringify(windows);
        const current = operationRef.current;
        const operationId =
          current !== null && current.fingerprint === fingerprint
            ? current.id
            : createModelCostOperationId();
        operationRef.current = { fingerprint, id: operationId };
        await onSave({
          identity,
          costMultiplier: cost.cost_multiplier,
          operationId,
          windows,
        });
      }}
    />
  );
}
